import React from "react";
import styled from "styled-components";
import { Video } from "../types";

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 20px;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 12px;
    max-width: 90vw;
    max-height: 90vh;
    overflow: hidden;
    position: relative;
    width: 800px;

    @media (max-width: 768px) {
        width: 100%;
        max-height: 80vh;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 15px;
    right: 15px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
    cursor: pointer;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.3s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.9);
    }
`;

const VideoContainer = styled.div`
    width: 100%;
    height: 0;
    padding-bottom: 56.25%; /* 16:9 aspect ratio */
    position: relative;
    background: #000;
`;

const VideoPlayer = styled.video`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
`;

const VideoInfo = styled.div`
    padding: 1.5rem;

    @media (max-width: 768px) {
        padding: 1rem;
    }
`;

const VideoTitle = styled.h2`
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #333;

    @media (max-width: 768px) {
        font-size: 1.2rem;
    }
`;

const VideoDescription = styled.p`
    color: #666;
    line-height: 1.6;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
        font-size: 0.9rem;
    }
`;

const VideoMeta = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.9rem;
    color: #999;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
    }
`;

const Category = styled.span`
    background: #667eea;
    color: white;
    padding: 0.3rem 0.8rem;
    border-radius: 15px;
    font-size: 0.8rem;
    font-weight: 500;
`;

const Date = styled.span`
    color: #999;
`;

interface VideoModalProps {
    video: Video | null;
    isOpen: boolean;
    onClose: () => void;
}

const VideoModal: React.FC<VideoModalProps> = ({ video, isOpen, onClose }) => {
    if (!isOpen || !video) return null;

    const formatDate = (dateString: string) => {
        const date = new window.Date(dateString);
        return date.toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <ModalOverlay onClick={handleOverlayClick}>
            <ModalContent>
                <CloseButton onClick={onClose}>✕</CloseButton>

                <VideoContainer>
                    <VideoPlayer controls>
                        <source src={video.videoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                    </VideoPlayer>
                </VideoContainer>

                <VideoInfo>
                    <VideoTitle>{video.title}</VideoTitle>
                    <VideoDescription>{video.description}</VideoDescription>
                    <VideoMeta>
                        <Category>{video.category}</Category>
                        <Date>{formatDate(video.createdAt)}</Date>
                    </VideoMeta>
                </VideoInfo>
            </ModalContent>
        </ModalOverlay>
    );
};

export default VideoModal;
